import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { AlunoFormDialog } from "@/components/forms/AlunoFormDialog";
import { InscricaoManager } from "@/components/InscricaoManager";
import { TipoBadge } from "@/components/TipoBadge";
import {
  ArrowLeft, Loader2, Plus, UserPlus, Users, ClipboardCheck, CheckCircle2,
  Clock, ChevronRight, Search, Check, QrCode, Dumbbell, Eye, Radio, WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { useCollabRoom } from "@/hooks/useCollabRoom";

export default function AplicacaoPainel() {
  const { turmaId, oficinaId } = useParams();
  const navigate = useNavigate();
  const backUrl = `/aplicacao/${turmaId}/${oficinaId}`;

  const [turma, setTurma] = useState(null);
  const [oficina, setOficina] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [starting, setStarting] = useState(null);

  const [novoOpen, setNovoOpen] = useState(false);
  const [existOpen, setExistOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [busca, setBusca] = useState("");
  const [sel, setSel] = useState([]);

  const load = useCallback(() => {
    setLoading(true); setErro(false);
    api.get(`/turmas/${turmaId}`).then(async (r) => {
      const t = r.data;
      setTurma(t);
      const [of, al, av, insc] = await Promise.all([
        api.get(`/oficinas/${oficinaId}`),
        api.get(`/alunos?turma_id=${turmaId}`),
        api.get(`/avaliacoes?turma_id=${turmaId}&oficina_id=${oficinaId}`),
        api.get(`/inscricoes?turma_id=${turmaId}&status=pendente`).catch(() => ({ data: [] })),
      ]);
      setOficina(of.data); setAlunos(al.data); setAvaliacoes(av.data); setPendentes(insc.data);
    }).catch(() => setErro(true)).finally(() => setLoading(false));
  }, [turmaId, oficinaId]);
  useEffect(() => { load(); }, [load]);

  // Sincronização colaborativa em tempo real (WebSocket + fallback polling)
  const { state: collab, connected } = useCollabRoom(turmaId, oficinaId, () => load());
  const lockByAluno = {};
  (collab.locks || []).forEach((l) => { lockByAluno[l.aluno_id] = l; });
  const meId = collab.me;

  // aluno_id -> melhor avaliação desta oficina (concluída tem prioridade; senão em andamento mais recente)
  const avPorAluno = {};
  for (const a of avaliacoes) {
    const cur = avPorAluno[a.aluno_id];
    if (!cur || (a.status === "concluida" && cur.status !== "concluida")) avPorAluno[a.aluno_id] = a;
  }

  const aAvaliar = alunos.filter((a) => avPorAluno[a.id]?.status !== "concluida");
  // Avaliados ordenados por média DECRESCENTE (maior primeiro). Empate: ordem alfabética (apenas visual).
  // Usa exclusivamente a média já calculada (resultado.media_geral); não recalcula nada.
  const mediaDe = (a) => { const m = avPorAluno[a.id]?.resultado?.media_geral; return m == null ? -Infinity : m; };
  const avaliados = alunos
    .filter((a) => avPorAluno[a.id]?.status === "concluida")
    .sort((x, y) => {
      if (mediaDe(y) !== mediaDe(x)) return mediaDe(y) - mediaDe(x);
      return (x.nome_guerra || x.nome || "").toLowerCase().localeCompare((y.nome_guerra || y.nome || "").toLowerCase());
    });
  // Colocação: médias iguais compartilham a mesma posição (empate = mesma colocação).
  const posByAluno = {};
  let _lastMedia = null;
  let _lastPos = 0;
  avaliados.forEach((a, idx) => {
    const m = avPorAluno[a.id]?.resultado?.media_geral ?? null;
    if (idx === 0 || m !== _lastMedia) { _lastPos = idx + 1; _lastMedia = m; }
    posByAluno[a.id] = _lastPos;
  });
  const proximo = aAvaliar.find((a) => { const lk = lockByAluno[a.id]; return !(lk && lk.owner_id !== meId); }) || null;

  const iniciar = async (aluno) => {
    const lk = lockByAluno[aluno.id];
    if (lk && lk.owner_id !== meId) { toast.error(`Este aluno está sendo avaliado por ${lk.owner_nome}.`); return; }
    setStarting(aluno.id);
    try {
      // Sempre passa pelo backend (idempotente + trava atômica). Se outro instrutor detém a
      // trava, o servidor responde 409 e o início é bloqueado (sem duplicar avaliação).
      const { data } = await api.post("/avaliacoes", {
        curso_id: turma.curso_id, turma_id: turmaId, oficina_id: oficinaId, aluno_id: aluno.id,
      }, { silent: true });
      navigate(`/avaliar/${data.id}?back=${encodeURIComponent(backUrl)}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setStarting(null); }
  };

  const confirmarInscrito = async (i) => {
    try { await api.post(`/inscricoes/${i.id}/aprovar`); toast.success(`${i.nome} adicionado(a) à instrução`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openExistentes = async () => {
    setSel([]); setBusca(""); setExistOpen(true);
    const { data } = await api.get("/alunos");
    setTodos(data);
  };
  const jaNaTurma = (a) => a.turma_id === turmaId || (a.turma_ids || []).includes(turmaId);
  const toggleSel = (aid) => setSel((s) => s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]);
  const vincular = async () => {
    let ok = 0;
    for (const aid of sel) {
      try { await api.post(`/turmas/${turmaId}/alunos/${aid}`); ok += 1; } catch (e) { /* já vinculado */ }
    }
    toast.success(`${ok} aluno(s) adicionado(s) à instrução`);
    setExistOpen(false); setSel([]); load();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (erro || !turma || !oficina) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Aplicação não encontrada ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate(`/turmas/${turmaId}`)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Voltar para a turma
      </button>
    </div>
  );

  const bt = busca.toLowerCase();
  const filtrados = todos.filter((a) => !bt || [a.nome, a.nome_guerra, a.rg, a.matricula, a.identificacao_funcional, a.instituicao_nome, a.unidade_nome].some((f) => (f || "").toLowerCase().includes(bt)));

  return (
    <div data-testid="aplicacao-painel">
      <button data-testid="voltar-btn" onClick={() => navigate(`/turmas/${turmaId}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {turma.nome}
      </button>
      <PageHeader title={oficina.nome} subtitle={`${turma.nome} · ${turma.curso_nome}`} />

      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="collab-contexto">
        <Badge className="bg-primary/15 text-primary border-primary/30"><Users className="h-3.5 w-3.5 mr-1" />Turma: {turma.nome}</Badge>
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40"><Dumbbell className="h-3.5 w-3.5 mr-1" />Oficina: {oficina.nome}</Badge>
      </div>
      <div className="flex items-center gap-3 mb-4 text-xs font-mono" data-testid="collab-status">
        <span className="text-muted-foreground">Avaliações <span className="text-foreground font-semibold" data-testid="collab-progress">{collab.progress?.avaliados ?? 0} / {collab.progress?.total ?? 0}</span> concluídas</span>
        <Badge className={connected ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}>
          {connected ? <><Radio className="h-3 w-3 mr-1" />Tempo real</> : <><WifiOff className="h-3 w-3 mr-1" />Reconectando…</>}
        </Badge>
      </div>

      <Card className="p-5 bg-card border-border mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <TipoBadge tipo={turma.curso_tipo || turma.curso?.tipo} />
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40"><Dumbbell className="h-3.5 w-3.5 mr-1" />{(oficina.criterios || []).length} critérios</Badge>
          {oficina.tipologia_cec_nome && <Badge className="bg-primary/15 text-primary border-primary/30">CEC: {oficina.tipologia_cec_nome}</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" data-testid="painel-novo-aluno-btn" onClick={() => setNovoOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Novo aluno</Button>
          <Button size="sm" variant="secondary" data-testid="painel-aluno-existente-btn" onClick={openExistentes}><UserPlus className="h-4 w-4 mr-1.5" />Adicionar aluno existente</Button>
        </div>
        <div className="mt-3"><InscricaoManager turma={turma} /></div>
      </Card>

      {/* Inscritos por link/QR aguardando confirmação */}
      {!!pendentes.length && (
        <Section title={`Inscritos por link / QR Code (${pendentes.length})`} icon={QrCode}>
          {pendentes.map((i) => (
            <div key={i.id} data-testid={`pendente-${i.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="p-2 rounded-full bg-sky-500/15 shrink-0"><QrCode className="h-4 w-4 text-sky-400" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{i.nome}{i.nome_guerra ? <span className="text-xs text-primary font-mono ml-1">"{i.nome_guerra}"</span> : ""}</div>
                <div className="text-xs text-muted-foreground truncate">{[i.grau_nome, i.identificacao, i.instituicao_nome, i.unidade_nome].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <Button size="sm" data-testid={`confirmar-inscrito-${i.id}`} onClick={() => confirmarInscrito(i)} className="shrink-0"><Check className="h-3.5 w-3.5 mr-1" />Adicionar à instrução</Button>
            </div>
          ))}
        </Section>
      )}

      {/* A AVALIAR */}
      {proximo && (
        <div className="mb-4">
          <Button data-testid="avaliar-proximo-btn" onClick={() => iniciar(proximo)} disabled={!!starting} className="w-full sm:w-auto">
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
            Avaliar próximo: {(proximo.grau_nome && proximo.grau_nome !== "Não se aplica" ? proximo.grau_nome + " " : "") + (proximo.nome_guerra || proximo.nome)}
          </Button>
        </div>
      )}
      <Section title={`A avaliar (${aAvaliar.length})`} icon={ClipboardCheck}>
        {aAvaliar.length ? aAvaliar.map((a) => {
          const av = avPorAluno[a.id];
          const emAnd = av?.status === "em_andamento";
          const lk = lockByAluno[a.id];
          const lockedByOther = lk && lk.owner_id !== meId;
          const lockedByMe = lk && lk.owner_id === meId;
          return (
            <button key={a.id} data-testid={`avaliar-aluno-${a.id}`} disabled={starting === a.id}
              onClick={() => lockedByOther ? toast.error(`Este aluno está sendo avaliado por ${lk.owner_nome}.`) : iniciar(a)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left disabled:opacity-60">
              <div className="p-2 rounded-full bg-amber-500/15 shrink-0"><Users className="h-4 w-4 text-amber-400" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{a.grau_nome && a.grau_nome !== "Não se aplica" ? `${a.grau_nome} ` : ""}{a.nome_guerra || a.nome}</div>
                <div className="text-xs text-muted-foreground truncate">{a.nome_guerra ? a.nome : (a.matricula || a.identificacao_funcional || "—")}</div>
              </div>
              {lockedByOther
                ? <Badge className="bg-purple-500/20 text-purple-200 border-purple-500/40 shrink-0" data-testid={`em-avaliacao-${a.id}`}><Clock className="h-3 w-3 mr-1" />Em avaliação por {lk.owner_nome}</Badge>
                : lockedByMe
                ? <Badge className="bg-primary/20 text-primary border-primary/40 shrink-0"><Clock className="h-3 w-3 mr-1" />Você está avaliando</Badge>
                : emAnd
                ? <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/40 shrink-0"><Clock className="h-3 w-3 mr-1" />Em andamento</Badge>
                : <Badge className="bg-secondary text-secondary-foreground border-border shrink-0">A avaliar</Badge>}
              {starting === a.id ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
            </button>
          );
        }) : <Empty>Todos os alunos vinculados já foram avaliados nesta oficina.</Empty>}
      </Section>

      {/* AVALIADOS */}
      <Section title={`Avaliados (${avaliados.length})`} icon={CheckCircle2}>
        {avaliados.length ? avaliados.map((a) => {
          const av = avPorAluno[a.id];
          const res = av?.resultado || {};
          const apto = res.aprovado;
          return (
            <div key={a.id} data-testid={`avaliado-${a.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="w-7 text-center font-heading font-extrabold text-sm text-primary shrink-0" data-testid={`posicao-${a.id}`}>{posByAluno[a.id]}º</div>
              <div className="p-2 rounded-full bg-emerald-500/15 shrink-0"><CheckCircle2 className="h-4 w-4 text-emerald-400" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{a.grau_nome && a.grau_nome !== "Não se aplica" ? `${a.grau_nome} ` : ""}{a.nome_guerra || a.nome}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-xs">
                  {res.media_geral != null && <span className="text-muted-foreground font-mono">Média {res.media_geral}</span>}
                  {res.nivel_geral?.label && <Badge className="bg-secondary text-secondary-foreground border-border">{res.nivel_geral.label}</Badge>}
                  {res.situacao && <Badge className={apto ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}>{res.situacao}</Badge>}
                </div>
              </div>
              <Button size="sm" variant="secondary" data-testid={`ver-avaliacao-${a.id}`} onClick={() => navigate(`/avaliacoes/${av.id}?back=${encodeURIComponent(backUrl)}`)} className="shrink-0"><Eye className="h-3.5 w-3.5 mr-1" />Ver</Button>
            </div>
          );
        }) : <Empty>Nenhum aluno avaliado ainda nesta oficina.</Empty>}
      </Section>

      <AlunoFormDialog open={novoOpen} onOpenChange={setNovoOpen} lockTurmaId={turmaId} onSaved={load} />

      <Dialog open={existOpen} onOpenChange={setExistOpen}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Adicionar aluno existente</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Selecione alunos já cadastrados para vincular a esta instrução (o cadastro original é preservado).</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="painel-busca-existente" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, nome de guerra, RG, matrícula..." className="pl-8" />
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {filtrados.map((a) => {
              const linked = jaNaTurma(a);
              const checked = sel.includes(a.id);
              return (
                <button key={a.id} data-testid={`painel-vincular-${a.id}`} disabled={linked} onClick={() => toggleSel(a.id)}
                  className={`w-full flex items-center justify-between gap-2 border rounded-md p-2.5 text-left ${linked ? "opacity-50 cursor-not-allowed border-border" : checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.nome}{a.nome_guerra ? <span className="text-xs text-primary font-mono ml-1">"{a.nome_guerra}"</span> : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">{[a.matricula, a.instituicao_nome, a.unidade_nome].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {linked
                    ? <span className="text-xs text-emerald-400 shrink-0 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Participante</span>
                    : checked ? <Check className="h-4 w-4 text-primary shrink-0" /> : <Plus className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>
              );
            })}
            {!filtrados.length && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum aluno encontrado.</p>}
          </div>
          <Button data-testid="painel-adicionar-selecionados-btn" onClick={vincular} disabled={!sel.length}>
            Adicionar {sel.length || ""} à instrução
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3"><Icon className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">{title}</h2></div>
      <Card className="bg-card border-border divide-y divide-border overflow-hidden">{children}</Card>
    </div>
  );
}
function Empty({ children }) {
  return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{children}</div>;
}
